import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { HiOutlineMenuAlt4 } from "react-icons/hi";
import { IoClose } from "react-icons/io5";
import { Brand } from "./Brand";
import { getToken } from "../api/client";

// Переход к якорю (#about, #contacts) с любой страницы
export function useAnchor() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  return (id, close) => (e) => {
    e.preventDefault();
    close?.();
    const go = () => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    if (id === "contacts" || pathname === "/") go();
    else {
      navigate("/");
      setTimeout(go, 350);
    }
  };
}

export function LangSwitch() {
  const { i18n } = useTranslation();
  const set = (lng) => {
    i18n.changeLanguage(lng);
    try {
      localStorage.setItem("language", lng);
    } catch {
      /* приватный режим */
    }
  };
  return (
    <div className="lang" role="group" aria-label="Language">
      {["ru", "en"].map((l) => (
        <button key={l} type="button" className={i18n.language === l ? "is-active" : ""} onClick={() => set(l)}>
          {l.toUpperCase()}
        </button>
      ))}
    </div>
  );
}

export default function SiteHeader() {
  const { t } = useTranslation();
  const { pathname, search } = useLocation();
  const anchor = useAnchor();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [isAdmin, setIsAdmin] = useState(Boolean(getToken()));

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    const onAuth = () => setIsAdmin(Boolean(getToken()));
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("rr:auth", onAuth);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("rr:auth", onAuth);
    };
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const close = () => setOpen(false);
  const country = new URLSearchParams(search).get("country");
  const active = (path, c) =>
    (c ? pathname === "/properties" && country === c : pathname.startsWith(path) && !(path === "/properties" && country)) ? "is-active" : "";

  const links = (onClick) => (
    <>
      <Link to="/properties" className={active("/properties")} onClick={onClick}>
        {t("site.nav.catalog")}
      </Link>
      <Link to="/properties?country=uae" className={active("", "uae")} onClick={onClick}>
        {t("site.nav.dubai")}
      </Link>
      <Link to="/properties?country=turkey" className={active("", "turkey")} onClick={onClick}>
        {t("site.nav.istanbul")}
      </Link>
      <Link to="/permanent-residency/ikamet" className={active("/permanent-residency")} onClick={onClick}>
        {t("site.nav.residence")}
      </Link>
      <a href="/#about" className="hide-md" onClick={anchor("about", onClick)}>
        {t("site.nav.about")}
      </a>
      <Link to="/news" className={`hide-md ${active("/news")}`} onClick={onClick}>
        {t("site.nav.news")}
      </Link>
      <a href="#contacts" onClick={anchor("contacts", onClick)}>
        {t("site.nav.contacts")}
      </a>
      {isAdmin && (
        <Link to="/admin" onClick={onClick}>
          {t("nav.adminPanel")}
        </Link>
      )}
    </>
  );

  return (
    <>
      <header className={`site-header ${scrolled ? "is-scrolled" : ""}`}>
        <div className="wrap">
          <div className="site-header__bar">
            <Brand />
            <nav className="site-nav" aria-label="Main">
              {links()}
            </nav>
            <div className="site-header__right">
              <LangSwitch />
              <a href="#contacts" className="btn-pill btn-pill--small" onClick={anchor("contacts")}>
                {t("site.nav.cta")}
              </a>
              <button type="button" className="burger" onClick={() => setOpen(true)} aria-label={t("site.nav.menu")}>
                <HiOutlineMenuAlt4 />
              </button>
            </div>
          </div>
        </div>
      </header>

      {open && (
        <div className="mobile-menu" role="dialog" aria-modal="true">
          <div className="mobile-menu__top">
            <Brand onClick={close} />
            <button type="button" className="burger" onClick={close} aria-label={t("site.nav.close")}>
              <IoClose />
            </button>
          </div>
          <nav>
            <Link to="/" onClick={close}>
              {t("site.nav.home")}
            </Link>
            {links(close)}
          </nav>
          <div className="mobile-menu__foot">
            <LangSwitch />
            <a href="#contacts" className="btn-pill btn-pill--light" onClick={anchor("contacts", close)}>
              {t("site.nav.cta")}
            </a>
          </div>
        </div>
      )}
    </>
  );
}
